import type { LazyTableProps } from '@vrplatform/table';
import { createContext, useContext, useState } from 'react';
import { type StoreApi, createStore, useStore } from 'zustand';
import {
  type TableFilterSlice,
  type TableSelectionSlice,
  createTableFilterSlice,
  createTableSelectionSlice,
} from '.';

type StoreProps = TableSelectionSlice &
  TableFilterSlice & { resetStore: () => void };

const context = createContext<StoreApi<StoreProps> | null>(null);

export function TableStoreProvider({
  children,
  filterType,
  selected,
}: {
  children: React.ReactNode;
  filterType: StoreProps['filterType'];
  selected?: StoreProps['selected'];
}) {
  const [store] = useState(
    createStore<StoreProps>()((set, get, store) => ({
      ...createTableFilterSlice(set, get, store),
      ...createTableSelectionSlice(set, get, store),
      resetStore: () => set(store.getInitialState()),
      filterType,
      selected: selected ?? {},
    }))
  );

  return <context.Provider value={store}>{children}</context.Provider>;
}

function useContextStore() {
  const store = useContext(context);
  if (!store) {
    throw new Error('TableStoreProvider not found');
  }

  return store;
}

export const useTableStoreReset = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.resetStore);
};

export const useTableStoreSelected = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.selected);
};

export const useTableStoreSelecting = <TRow extends Record<string, unknown>>(
  options?: Omit<
    NonNullable<LazyTableProps<TRow>['selecting']>,
    'rowSelection' | 'setAllPagesSelected' | 'setRowSelection'
  >
): LazyTableProps<TRow>['selecting'] => {
  const store = useContextStore();
  const rows = useStore(store, (state) => state.selected);
  const setRowSelection = useStore(store, (state) => state.setSelected);
  const allPagesSelected = useStore(store, (state) => state.allPagesSelected);
  const setAllPagesSelected = useStore(
    store,
    (state) => state.setAllPagesSelected
  );

  return {
    ...options,
    rowSelection: {
      rows,
      allPagesSelected,
    },
    setAllPagesSelected,
    setRowSelection,
  };
};

export const useTableStoreGetSelectionState = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.getSelectionState);
};

export const useTableStoreHasSelected = () => {
  const store = useContextStore();
  return useStore(store, (state) => {
    if (state.allPagesSelected) return true;
    return Object.values(state.selected).some((value) => value);
  });
};

export const useTableStoreSetSelected = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.setSelected);
};

export const useTableStoreAllPagesSelected = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.allPagesSelected);
};

export const useTableStoreSetAllPagesSelected = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.setAllPagesSelected);
};

export const useTableStoreFilter = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.filter);
};

export const useTableStoreSetFilter = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.setFilter);
};

export const useTableStoreFilterType = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.filterType);
};

export const useTableStoreSetFilterType = () => {
  const store = useContextStore();
  return useStore(store, (state) => state.setFilterType);
};
